import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Crosshair, Maximize2, Network, ZoomIn, ZoomOut } from "lucide-react";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import { useSettings } from "../../app/settings";
import type { FilterAst } from "../../core/filter";
import { FILTER_SCHEMA } from "../shared";
import { DEFAULT_EDGES, buildElements, type EdgeKind, type Elements, type Point } from "./model";
import { loadViewState, saveViewState } from "./viewState";
import type { GraphLayout, GraphRenderer, GraphScene, SceneCallbacks } from "./scene";

const FlowGraph = lazy(() => import("./FlowGraph"));

export interface GraphConfig {
  filter?: FilterAst;
  /** which relations to draw */
  edges?: EdgeKind[];
  layout?: GraphLayout;
}

/** one step of the + and − buttons, as in KSS */
const ZOOM_STEP = 1.3;

/** Changes when the effective theme or colour mode changes, so the renderers can restyle in place. */
function useThemeKey(): string {
  const read = () => {
    const d = document.documentElement.dataset;
    return `${d.theme ?? ""}|${d.plainColors ?? ""}|${matchMedia("(prefers-color-scheme: dark)").matches}`;
  };
  const [key, setKey] = useState(read);
  useEffect(() => {
    const update = () => setKey(read());
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-plain-colors"],
    });
    const mq = matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", update);
    return () => {
      mo.disconnect();
      mq.removeEventListener("change", update);
    };
  }, []);
  return key;
}

const isDark = () => {
  const t = document.documentElement.dataset.theme;
  return t ? t === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
};

/**
 * Task graph (spec §9.7 `graph`): nodes = tasks, done = grey, `relates` dashed. Two engines behind one
 * contract (Cytoscape, React Flow). A change in the workspace adds, removes or updates only the nodes and
 * edges it concerns; nothing moves the view except the person and, when switched on, auto-centre
 * (§9.19 no surprises; KSS ADR 0010). Zoom, pan, positions and the toggle are remembered per device.
 */
export function GraphView({ config, ctx, widgetId }: ViewProps<GraphConfig>) {
  const { t } = useTranslation();
  const kinds = config.edges ?? DEFAULT_EDGES;
  // one engine for every graph, chosen in Settings → How it feels
  const renderer: GraphRenderer = useSettings(ctx.handle).graph.renderer;
  const requested: GraphLayout = config.layout ?? "dagre";
  const layout: GraphLayout = renderer === "reactflow" ? "dagre" : requested;
  const kindsKey = kinds.join(",");
  const elements = useDocSelector(
    ctx.handle,
    (doc) => buildElements(doc, config.filter, kindsKey.split(",") as EdgeKind[], Date.now()),
    [config.filter, kindsKey],
  );
  const stateKey = widgetId;
  const [autoCentre, setAutoCentre] = useState(() => loadViewState(stateKey).autoCentre ?? true);
  const [zoom, setZoom] = useState<{ level: number; min: number; max: number } | null>(null);
  const [ready, setReady] = useState(0);
  const scene = useRef<GraphScene | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<Elements | null>(null);
  useEffect(() => {
    elementsRef.current = elements ?? null;
  }, [elements]);
  const themeKey = useThemeKey();
  const reduced =
    document.documentElement.dataset.reducedMotion === "true" ||
    matchMedia("(prefers-reduced-motion: reduce)").matches;

  const open = useRef(ctx.openTask);
  useEffect(() => {
    open.current = ctx.openTask;
  });
  const callbacks = useMemo<SceneCallbacks>(
    () => ({
      onOpen: (id) => open.current(id),
      onZoom: setZoom,
      onViewport: (v) => saveViewState(stateKey, { zoom: v.zoom, pan: v.pan }),
      onPositions: (p: Record<string, Point>) => {
        const ids = new Set((elementsRef.current?.nodes ?? []).map((n) => n.id));
        const merged = { ...(loadViewState(stateKey).positions ?? {}), ...p };
        for (const id of Object.keys(merged)) if (!ids.has(id)) delete merged[id];
        saveViewState(stateKey, { positions: merged, layout });
      },
    }),
    [stateKey, layout],
  );

  /** what the renderer starts from: remembered view and positions (not if the layout was changed) */
  const initial = useCallback(() => {
    const vs = loadViewState(stateKey);
    return vs.layout && vs.layout !== layout ? { ...vs, positions: undefined } : vs;
  }, [stateKey, layout]);

  const hasElements = elements !== undefined;
  // Cytoscape: one instance per (engine, layout); data changes are applied below, never rebuilt
  useEffect(() => {
    if (renderer !== "cytoscape" || !hasElements || !host.current) return;
    let dead = false;
    let made: GraphScene | null = null;
    void import("./cyScene").then(({ createCyScene }) => {
      if (dead || !host.current || !elementsRef.current) return;
      made = createCyScene(
        host.current,
        { elements: elementsRef.current, layout, autoCentre, initial: initial(), reducedMotion: reduced },
        callbacks,
      );
      scene.current = made;
      setReady((r) => r + 1);
    });
    return () => {
      dead = true;
      made?.dispose();
      scene.current = null;
    };
    // autoCentre and reduced are read once at creation; later changes go through the scene
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderer, layout, hasElements, callbacks, initial]);

  useEffect(() => {
    if (elements) scene.current?.apply(elements);
  }, [elements, ready]);
  useEffect(() => {
    scene.current?.restyle();
  }, [themeKey, ready]);
  useEffect(() => {
    if (box.current)
      (box.current as HTMLDivElement & { __drangGraph?: () => GraphScene | null }).__drangGraph = () =>
        scene.current;
  });

  const toggleAutoCentre = () => {
    const next = !autoCentre;
    setAutoCentre(next);
    scene.current?.setAutoCentre(next);
    saveViewState(stateKey, { autoCentre: next });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "+" || e.key === "=") scene.current?.zoomBy(ZOOM_STEP);
    else if (e.key === "-") scene.current?.zoomBy(1 / ZOOM_STEP);
    else if (e.key === "0") scene.current?.resetScale();
    else return;
    e.preventDefault();
  };

  const labelOf = (id: string) => elements?.nodes.find((n) => n.id === id)?.label ?? id;
  return (
    <div
      ref={box}
      className="kss-surface relative h-full overflow-hidden"
      data-testid="graph-view"
      data-renderer={renderer}
      data-nodes={elements?.nodes.length ?? 0}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label={t("views.graph.label")}
    >
      {elements && elements.nodes.length === 0 && (
        <p className="absolute inset-0 m-auto h-fit text-center text-[12.5px] text-neutral-500">
          {t("graph.empty")}
        </p>
      )}
      {renderer === "cytoscape" ? (
        <div ref={host} className="h-full w-full" role="img" aria-label={t("views.graph.label")} />
      ) : (
        elements && (
          <div className="h-full w-full" data-testid="graph-flow">
            <Suspense fallback={null}>
              <FlowGraph
                key={`${layout}|${stateKey ?? ""}`}
                options={{ elements, layout, autoCentre, initial: initial(), reducedMotion: reduced }}
                callbacks={callbacks}
                onReady={(s) => {
                  scene.current = s;
                  setReady((r) => r + 1);
                }}
                dark={isDark()}
              />
            </Suspense>
          </div>
        )
      )}
      <div
        className="absolute right-2 top-2 z-10 flex gap-1 rounded-md border border-neutral-200 bg-[var(--surface)] p-0.5 shadow-[var(--shadow-sm)] dark:border-neutral-700"
        role="toolbar"
        aria-label={t("graph.toolbar")}
        data-testid="graph-toolbar"
      >
        <ToolButton
          label={t("graph.zoomIn")}
          testId="graph-zoom-in"
          disabled={!!zoom && zoom.level >= zoom.max - 1e-6}
          onClick={() => scene.current?.zoomBy(ZOOM_STEP)}
        >
          <ZoomIn size={14} aria-hidden />
        </ToolButton>
        <ToolButton
          label={t("graph.zoomOut")}
          testId="graph-zoom-out"
          disabled={!!zoom && zoom.level <= zoom.min + 1e-6}
          onClick={() => scene.current?.zoomBy(1 / ZOOM_STEP)}
        >
          <ZoomOut size={14} aria-hidden />
        </ToolButton>
        <ToolButton
          label={t("graph.resetScale")}
          testId="graph-reset-scale"
          onClick={() => scene.current?.resetScale()}
        >
          <span className="font-mono text-[10.5px]">1:1</span>
        </ToolButton>
        <ToolButton label={t("graph.fit")} testId="graph-fit" onClick={() => scene.current?.fit()}>
          <Maximize2 size={14} aria-hidden />
        </ToolButton>
        <ToolButton
          label={t("graph.autoCentre")}
          testId="graph-autocentre"
          pressed={autoCentre}
          onClick={toggleAutoCentre}
        >
          <Crosshair size={14} aria-hidden />
        </ToolButton>
        <ToolButton
          label={t("graph.rearrange")}
          testId="graph-rearrange"
          onClick={() => scene.current?.rearrange()}
        >
          <Network size={14} aria-hidden />
        </ToolButton>
      </div>
      {renderer === "reactflow" && requested === "cose" && (
        <p
          className="absolute bottom-2 left-2 z-10 rounded-sm bg-[var(--surface)] px-2 py-1 text-[11.5px] text-neutral-500"
          data-testid="graph-layout-note"
        >
          {t("graph.forceOnlyCytoscape")}
        </p>
      )}
      <ul className="sr-only">
        {elements?.edges.map((e) => (
          <li key={e.id}>{t(`graph.edge.${e.kind}`, { from: labelOf(e.source), to: labelOf(e.target) })}</li>
        ))}
      </ul>
    </div>
  );
}

function ToolButton({
  label,
  testId,
  disabled,
  pressed,
  onClick,
  children,
}: {
  label: string;
  testId: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick(): void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={clsx(
        "grid h-7 w-7 place-items-center rounded-sm text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-neutral-800",
        pressed && "bg-accent/15 text-accent dark:bg-accent/25",
      )}
    >
      {children}
    </button>
  );
}

registerViewType<GraphConfig>({
  id: "graph",
  label: "views.graph.label",
  icon: "share-2",
  level: "standard",
  defaultConfig: { edges: DEFAULT_EDGES, layout: "dagre" },
  configSchema: {
    type: "object",
    properties: {
      layout: { type: "string", title: "config.layout", enum: ["dagre", "cose"] },
      edges: { type: "array", title: "config.edges" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: GraphView,
  minSize: { w: 4, h: 5 },
});
